import { Grid, Typography, Divider, Box, Paper, Card, Tooltip, Icon } from "@mui/material";
import { useIntl } from "react-intl";
import { Spinner } from "react-bootstrap";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import MDBox from "components/MDBox";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import { numericFormatter } from "react-number-format";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import CategoriesList from "examples/Lists/CategoriesList";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import MDBadgeDot from "components/MDBadgeDot";
import PieChart from "examples/Charts/PieChart";
import MDButton from "components/MDButton";
import { IconCalendarTime, IconPigMoney, IconReportMoney } from "@tabler/icons-react";

interface EstadisticaItem {
  actual: number;
  anterior: number;
  porcentaje: number;
  tendencia: "subida" | "bajada" | "igual";
}

interface UnidadMedida {
  id: number;
  nombre: string;
}

interface Categoria {
  id: number;
  nombre: string;
}

interface Almacen {
  id: number;
  nombre: string;
}

interface Ubicacion {
  id: number;
  nombre: string;
  id_almacen: number;
  almacen: Almacen;
}

interface ProductoSinMovimiento {
  id: number;
  clave: string;
  nombre: string;
  cantidad: number;
  minimos: number;
  maximos: number;
  id_unidad_medida: number;
  id_categoria: number;
  unidad_medida: UnidadMedida;
  categoria: Categoria;
}

interface ProductoStockMinimo extends ProductoSinMovimiento {
  punto_reorden: number;
  id_ubicacion: number;
  ubicacion: Ubicacion;
}

interface Proveedor {
  id_proveedor: number;
  razon_social: string;
  nombre_comercial: string;
  total_ordenes: number;
  monto_total: string;
}

interface RequisicionSinAtender {
  id: number;
  nombre_creador: string;
  nombre_usuario: string;
  created_at: string;
  estatus: number;
  dias_sin_atender: number;
}

interface CompraUltimos30Dias {
  id_inventario: number;
  nombre: string;
  clave: string;
  total_cantidad: string;
  monto_total: string;
  total_movimientos: number;
}

interface TopSolicitante {
  id_usuario: number;
  nombre_creador: string;
  total_solicitudes: number;
}

interface EstadisticasComprasData {
  gasto_mensual: EstadisticaItem;
  pendientes_cotizacion: number;
  pendientes_compra: number;
  material_sin_movimiento: ProductoSinMovimiento[];
  top_proveedores_30_dias: Proveedor[];
  requisiciones_sin_atender: RequisicionSinAtender[];
  compras_ultimos_30_dias: CompraUltimos30Dias[];
  top_solicitantes: TopSolicitante[];
  top_proyectos: null;
  productos_stock_minimo: ProductoStockMinimo[];
  ranking_proveedores: Proveedor[];
}

interface EstadisticasComprasModuloProps {
  verEstadisticas?: EstadisticasComprasData | null;
  usuarioArjion?: string | null;
  procesando?: boolean;
}

const EstadisticasComprasModulo = ({
  verEstadisticas,
  usuarioArjion,
  procesando,
}: EstadisticasComprasModuloProps) => {
  const intl = useIntl();

  if (!verEstadisticas && !procesando) return null;

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ color: "#004c93", textAlign: { xs: "center" } }}
              >
                Estadísticas compras
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />

          {procesando ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: "general_cargando_datos" })}
              </Typography>
              <Spinner animation="border" />
            </Box>
          ) : verEstadisticas ? (
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12} sm={3}>
                <ComplexStatisticsCard
                  color="success"
                  icon={<IconPigMoney size={36} stroke={2} />}
                  title="Gasto mensual"
                  colorHexa={"#041d45"}
                  count={numericFormatter(verEstadisticas?.gasto_mensual?.actual + "", {
                    thousandSeparator: ",",
                    decimalScale: 2,
                    fixedDecimalScale: false,
                    prefix: "$",
                  })}
                  percentage={{
                    color:
                      verEstadisticas?.gasto_mensual?.tendencia === "subida"
                        ? "success"
                        : verEstadisticas?.gasto_mensual?.tendencia === "bajada"
                          ? "error"
                          : "warning",
                    amount: `${verEstadisticas?.gasto_mensual?.tendencia === "subida"
                      ? "+"
                      : verEstadisticas?.gasto_mensual?.tendencia === "bajada"
                        ? "-"
                        : ""
                      }${verEstadisticas?.gasto_mensual?.porcentaje}%`,
                    label: "vs mes anterior",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <ComplexStatisticsCard
                  color="error"
                  icon={<IconCalendarTime size={36} stroke={2} />}
                  title="Requisiciones"
                  colorHexa={"#041d45"}
                  count={verEstadisticas?.pendientes_cotizacion}
                  percentage={{
                    color: "success",
                    amount: "",
                    label: "Pendientes de cotización",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <ComplexStatisticsCard
                  color="info"
                  icon={<IconReportMoney size={36} stroke={2} />}
                  title="Requisiciones"
                  colorHexa={"#041d45"}
                  count={verEstadisticas?.pendientes_cotizacion}
                  percentage={{
                    color: "success",
                    amount: "",
                    label: "Pendientes de compra",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ height: "100%" }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pt={2}
                    px={2}
                  >
                    <Typography variant="h6">Compras últimos 30 días</Typography>
                  </MDBox>
                  <MDBox mt={3}>
                    <Grid container alignItems="center">
                      <Grid item xs={7}>
                        <PieChart
                          chart={{
                            labels:
                              verEstadisticas?.compras_ultimos_30_dias.map((p) => p.nombre) ?? [],
                            datasets: {
                              label: "Monto total",
                              backgroundColors: [
                                "info",
                                "primary",
                                "dark",
                                "secondary",
                                "warning",
                                "error",
                                "success",
                              ],
                              data:
                                verEstadisticas?.compras_ultimos_30_dias.map(
                                  (p) => p.total_movimientos
                                ) ?? [],
                            },
                          }}
                          height="12.5rem"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <MDBox pr={1}>
                          {verEstadisticas?.compras_ultimos_30_dias.map((producto, index) => {
                            const colors = [
                              "info",
                              "primary",
                              "dark",
                              "secondary",
                              "warning",
                              "error",
                              "success",
                            ];
                            return (
                              <MDBox mb={1} key={producto.id_inventario}>
                                <MDBadgeDot
                                  color={colors[index % colors.length] as any}
                                  size="sm"
                                  badgeContent={`${producto.nombre}`}
                                />
                              </MDBox>
                            );
                          })}
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <CategoriesList
                  title="Materiales sin movimiento"
                  colorHexa="#041d45"
                  itemsPerPage={10}
                  categories={
                    verEstadisticas?.material_sin_movimiento?.map((material) => ({
                      color: "dark" as const,
                      icon: "inventory_2",
                      name: material.nombre,
                      description: (
                        <>
                          {material.clave} &bull; {material.categoria?.nombre ?? "Sin categoría"}
                        </>
                      ),
                      route: "/catalogo-productos",
                    })) ?? []
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CategoriesList
                  title="Requisiciones sin atender"
                  colorHexa="#041d45"
                  categories={
                    verEstadisticas?.requisiciones_sin_atender?.map((requisicion) => ({
                      color: "error" as const,
                      icon: "warning",
                      name: `Requisición #${requisicion.id}`,
                      description: (
                        <>
                          {requisicion.nombre_creador} &bull;{" "}
                          <Typography variant="caption" color="text" fontWeight="medium">
                            {Math.floor(requisicion.dias_sin_atender)} días sin atender
                          </Typography>
                        </>
                      ),
                      route: "/",
                    })) ?? []
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CategoriesList
                  title="Top solicitantes"
                  colorHexa="#041d45"
                  categories={
                    verEstadisticas?.top_solicitantes?.map((solicitante) => ({
                      color: "primary" as const,
                      icon: "assignment_add",
                      name: solicitante?.nombre_creador,
                      description: (
                        <>
                          Total de solicitudes:<strong>{solicitante?.total_solicitudes}</strong>{" "}
                        </>
                      ),
                      route: "/",
                    })) ?? []
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CategoriesList
                  title="Productos con stock minimo"
                  colorHexa="#041d45"
                  categories={
                    verEstadisticas?.productos_stock_minimo?.map((producto) => ({
                      color: "info" as const,
                      icon: "inventory",
                      name: producto?.nombre,
                      description: (
                        <>
                          {producto.clave} &bull; {producto.categoria?.nombre ?? "Sin categoría"} &bull;{" "}
                          <Typography variant="caption" color="text" fontWeight="medium">
                            <br />Cantidad: {producto.cantidad}
                            <br />Mínimo: {producto.minimos}
                          </Typography>
                        </>
                      ),
                      route: "/",
                    })) ?? []
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <VerticalBarChart
                  icon={{ color: "info", component: "leaderboard" }}
                  title="Top Proveedores (últimos 30 días)"
                  description="Total de ordenes realizadas"
                  colorHexa="#041d45"
                  chart={{
                    labels:
                      verEstadisticas?.top_proveedores_30_dias.map((p) => p.nombre_comercial) ?? [],
                    datasets: [
                      {
                        label: "Total de ordenes",
                        color: "dark",

                        data:
                          verEstadisticas?.top_proveedores_30_dias.map((p) => p.total_ordenes) ??
                          [],
                      },
                    ],
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <VerticalBarChart
                  icon={{ color: "info", component: "leaderboard" }}
                  title="Top Proveedores de todos los tiempos"
                  description="Total de ordenes realizadas"
                  colorHexa="#041d45"
                  chart={{
                    labels:
                      verEstadisticas?.ranking_proveedores.map((p) => p.nombre_comercial) ?? [],
                    datasets: [
                      {
                        label: "Total de ordenes",
                        color: "dark",
                        data:
                          verEstadisticas?.ranking_proveedores.map((p) => p.total_ordenes) ?? [],
                      },
                    ],
                  }}
                />
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: "no_hay_datos_registrados" })}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box >
  );
};

export default EstadisticasComprasModulo;
